import type { DurationPreset, NamedChoice } from "./types.js";

export const RESOURCES = [
  { id: "metal", name: "Metal", stored: true },
  { id: "research-points", name: "Research Points", stored: true },
  { id: "power", name: "Power", stored: false },
  { id: "construction-work", name: "Construction Work", stored: false },
  { id: "assembly-work", name: "Assembly Work", stored: false },
  { id: "supply", name: "Supply", stored: false },
  { id: "shield-charge", name: "Shield Charge", stored: true },
] as const;

export const DURATION_PRESETS = [
  { id: "lunch-shift", name: "Lunch Shift", limitMinutes: 45, openingPeaceMinutes: 3 },
  { id: "standard-shift", name: "Standard Shift", limitMinutes: 90, openingPeaceMinutes: 5 },
  { id: "double-shift", name: "Double Shift", limitMinutes: 150, openingPeaceMinutes: 8 },
] as const satisfies readonly DurationPreset[];

export const CLOCK_SPEEDS = [1, 2] as const;

export const EVENT_SETTINGS = [
  { id: "off", name: "Valley Incidents: Off" },
  { id: "announced", name: "Announced Incidents" },
] as const satisfies readonly NamedChoice[];

export const MODES = [
  { id: "every-town-for-itself", name: "Every Town for Itself" },
  { id: "team-shift", name: "Team Shift" },
  { id: "private-practice", name: "Private Practice" },
] as const satisfies readonly NamedChoice[];

export const MAPS = [
  { id: "duel", name: "Duel", minimumSeats: 2, evenSeats: false },
  { id: "ring", name: "Ring", minimumSeats: 3, evenSeats: false },
  { id: "crossroads", name: "Crossroads", minimumSeats: 6, evenSeats: true },
] as const;

export const STANDING_ORDERS = [
  { id: "hold", name: "Hold" },
  { id: "assault", name: "Assault" },
  { id: "raid", name: "Raid" },
] as const satisfies readonly NamedChoice[];

export const BOT_DIFFICULTIES = [
  { id: "apprentice", name: "Apprentice", decisionSeconds: 8, projectionSeconds: 0 },
  { id: "foreman", name: "Foreman", decisionSeconds: 3, projectionSeconds: 30 },
  { id: "superintendent", name: "Superintendent", decisionSeconds: 1, projectionSeconds: 60 },
] as const;

export const BOT_PERSONALITIES = [
  { id: "builder", name: "Builder" },
  { id: "defender", name: "Defender" },
  { id: "raider", name: "Raider" },
  { id: "siege-planner", name: "Siege Planner" },
  { id: "broker", name: "Broker" },
] as const satisfies readonly NamedChoice[];

export const INCIDENTS = [
  { id: "meteor-warning", name: "Meteor Warning" },
  { id: "valley-salvage-train", name: "Valley Salvage Train" },
  { id: "clear-skies", name: "Clear Skies" },
] as const satisfies readonly NamedChoice[];

export const EFFECT_TARGETS = [
  "mining-rig-output",
  "construction-yard-output",
  "producer-factory-output",
  "producer-factory-metal-demand",
  "assembly-work",
  "power-generator-output",
  "research-lab-output",
  "guard-troop-damage",
  "raider-troop-damage",
  "guard-integrity",
  "raider-integrity",
  "siege-shield-damage",
  "siege-building-damage",
  "shield-recharge",
  "shield-capacity",
  "supply-depot-capacity",
  "military-travel-time",
  "non-hq-building-integrity",
  "raider-infrastructure-leak",
] as const;

export const RATE_LABELS = Object.freeze({
  gross: "Gross",
  consumption: "Consumption",
  net: "Net",
});

export const FIXED_STRUCTURES = [
  { id: "headquarters", name: "Headquarters", initialIntegrity: 10_000, repairable: false },
  {
    id: "emergency-workshop",
    name: "Emergency Workshop",
    initialIntegrity: null,
    repairable: false,
    outputs: { metalPerSecond: 1, constructionWorkPerSecond: 1, power: 10 },
  },
] as const;

export const LIMITS = Object.freeze({
  supportedSeats: 64,
  supportedHumanPeers: 8,
  constructionQueue: 32,
  restorationQueue: 32,
  researchQueue: 16,
  discreteOrdersPerSeatBoundary: 16,
  retainedEvents: 2_000,
});

export const INITIAL_TOWN = Object.freeze({
  headquartersIntegrity: 10_000,
  metal: 300,
  researchPoints: 0,
  emergencyWorkshop: { metalPerSecond: 1, constructionWorkPerSecond: 1, power: 10 },
  availablePower: 50,
  requestedPower: {
    industry: 14,
    defenses: 4,
    militaryProduction: 4,
    research: 3,
    total: 25,
  },
  powerPriority: ["industry", "defenses", "military-production", "research"],
  metalPriority: ["construction", "producer-factories", "troop-assembly"],
  constructionOrder: { target: "mining-rig", repeat: true },
  assemblyOrder: { target: "guard-robot", repeat: true },
  factoryWorksTarget: "rig-factory",
  borderOrder: "hold",
  autoReinforce: false,
  constructionAllocation: { newConstruction: 100, restoration: 0 },
});

export type MapId = (typeof MAPS)[number]["id"];

export function defaultMap(seatCount: number): MapId {
  return seatCount === 2 ? "duel" : "ring";
}

export function equalAllocations(ids: readonly string[]): Readonly<Record<string, number>> {
  if (ids.length === 0) return Object.freeze({});
  const sorted = [...ids].sort();
  const base = Math.floor(100 / sorted.length);
  let remainder = 100 - base * sorted.length;
  return Object.freeze(
    Object.fromEntries(
      sorted.map((id) => {
        const value = base + (remainder > 0 ? 1 : 0);
        remainder -= remainder > 0 ? 1 : 0;
        return [id, value];
      }),
    ),
  );
}

export function topology(
  mapId: MapId,
  seatIds: readonly string[],
): readonly (readonly [string, string])[] {
  const unique = new Set(seatIds);
  if (unique.size !== seatIds.length) throw new TypeError("Topology seat IDs must be unique");
  if (mapId === "duel") {
    if (seatIds.length !== 2) throw new TypeError("Duel requires exactly two seats");
    const edge = [seatIds[0] as string, seatIds[1] as string].sort() as [string, string];
    return Object.freeze([edge]);
  }
  if (seatIds.length < 3) throw new TypeError("Ring requires at least three seats");
  if (mapId === "crossroads" && (seatIds.length < 6 || seatIds.length % 2 !== 0)) {
    throw new TypeError("Crossroads requires an even number of at least six seats");
  }
  const edges = new Map<string, readonly [string, string]>();
  const add = (left: string, right: string) => {
    const edge = [left, right].sort() as [string, string];
    edges.set(`${edge[0]}:${edge[1]}`, edge);
  };
  seatIds.forEach((seat, index) => {
    add(seat, seatIds[(index + 1) % seatIds.length] as string);
  });
  if (mapId === "crossroads") {
    seatIds.forEach((seat, index) => {
      add(seat, seatIds[(index + seatIds.length / 2) % seatIds.length] as string);
    });
  }
  return Object.freeze(
    [...edges.values()].sort((left, right) => left.join().localeCompare(right.join())),
  );
}
